import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { TASK_STATUS } from '../../utils/constants';
import { toISODateOrNull, formatDateForDateInput, getTodayDateString } from '../../utils/dateUtils';
import './TaskForm.css';

const TaskForm = ({ task, caseFiles = [], users = [], onSubmit, onCancel, loading, hideCaseFileSelection = false, caseFileId = null, editMode = false }) => {
  const { user } = useAuth();
  const [caseFileSearch, setCaseFileSearch] = useState('');
  const [formData, setFormData] = useState({
    CaseFileId: '',
    AssignedToUserId: '',
    Description: '',
    Response: '',
    DueDate: '',
    Status: TASK_STATUS.InProgress,
    TaskId: null,
  });

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  const isCreating = !task;
  const isCompleting = task && !isAdmin() && !editMode; // Avukat görevi tamamlıyor -> Kontrole Gönder
  const isAdminCompleting = task && isAdmin() && !editMode; // Admin görevi doğrudan tamamlandı yapıyor
  const isAdminEditing = task && isAdmin() && editMode; // Admin görevi düzenliyor

  useEffect(() => {
    if (task) {
      const dueDate = task.DueDate ?? task.dueDate;
      setFormData({
        CaseFileId: task.CaseFileId ?? task.caseFileId ?? '',
        AssignedToUserId: task.AssignedToUserId ?? task.assignedToUserId ?? '',
        Description: task.Description ?? task.description ?? '',
        Response: task.Response ?? task.response ?? '',
        DueDate: formatDateForDateInput(dueDate),
        Status: task.Status ?? task.status ?? TASK_STATUS.InProgress,
        TaskId: task.Id ?? task.id ?? null,
      });
    } else if (hideCaseFileSelection && caseFileId) {
      // Dosya detay sayfasından açıldıysa, dosya ID'sini otomatik set et
      setFormData(prev => ({
        ...prev,
        CaseFileId: caseFileId
      }));
    }
  }, [task, hideCaseFileSelection, caseFileId]);

  // Dosya listesi: Föy No'ya göre sıralı, müvekkil/föy no aramasına göre filtrelenmiş
  const sortedAndFilteredCaseFiles = useMemo(() => {
    let list = [...caseFiles];
    // Föy No'ya göre sırala (sayısal veya metin)
    list.sort((a, b) => {
      const aFoy = a.FoyNo ?? a.foyNo;
      const bFoy = b.FoyNo ?? b.foyNo;
      const aNum = parseInt(aFoy, 10);
      const bNum = parseInt(bFoy, 10);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
      return String(aFoy ?? '').localeCompare(String(bFoy ?? ''), 'tr');
    });
    // Müvekkil veya Föy No ile filtrele
    const search = (caseFileSearch || '').trim().toLowerCase();
    if (search) {
      list = list.filter((file) => {
        const muvekkil = (file.Muvekkil ?? file.muvekkil ?? '').toLowerCase();
        const foyNo = String(file.FoyNo ?? file.foyNo ?? '');
        return muvekkil.includes(search) || foyNo.toLowerCase().includes(search);
      });
    }
    return list;
  }, [caseFiles, caseFileSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'Status' || name === 'CaseFileId' || name === 'AssignedToUserId' 
        ? (value ? parseInt(value) : '') 
        : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isCreating) {
      if (!hideCaseFileSelection && (!formData.CaseFileId || formData.CaseFileId === '')) {
        alert('Lütfen listeden bir dosya seçin.');
        return;
      }
      // Admin görev oluşturuyor - Her zaman "İşlemde" durumunda başlar
      const finalCaseFileId = hideCaseFileSelection && caseFileId ? caseFileId : parseInt(formData.CaseFileId, 10);
      const dueDateValue = formData.DueDate && /^\d{4}-\d{2}-\d{2}$/.test(formData.DueDate)
        ? `${formData.DueDate}T23:59`
        : formData.DueDate;
      onSubmit({
        CaseFileId: finalCaseFileId,
        AssignedToUserId: parseInt(formData.AssignedToUserId),
        Description: formData.Description,
        DueDate: toISODateOrNull(dueDateValue),
        // Status gönderilmez, backend'de otomatik InProgress olarak ayarlanır
      });
    } else if (isAdminEditing) {
      // Admin düzenleme - Description, AssignedToUserId, DueDate
      const dueDateValue = formData.DueDate && /^\d{4}-\d{2}-\d{2}$/.test(formData.DueDate)
        ? `${formData.DueDate}T23:59`
        : formData.DueDate;
      onSubmit({
        TaskId: formData.TaskId,
        Description: formData.Description,
        AssignedToUserId: formData.AssignedToUserId ? parseInt(formData.AssignedToUserId) : null,
        DueDate: toISODateOrNull(dueDateValue),
      });
    } else {
      // Avukat: Kontrole Gönder (UnderReview) | Admin: Doğrudan Tamamlandı (Completed)
      onSubmit({
        TaskId: formData.TaskId,
        Response: formData.Response,
        Status: isAdminCompleting ? TASK_STATUS.Completed : TASK_STATUS.UnderReview,
      });
    }
  };

  return (
    <div className="task-form-overlay">
      <div className="task-form">
        <h2>
          {isCreating ? 'Yeni Görev Ata' : isCompleting ? 'Kontrole Gönder' : isAdminCompleting ? 'Tamamlandı Yap' : isAdminEditing ? 'Görev Düzenle' : 'Görev Düzenle'}
        </h2>
        <form onSubmit={handleSubmit}>
          {isCreating ? (
            <>
              {!hideCaseFileSelection && (
                <div className="form-group">
                  <label>Dosya (Foy No) *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Müvekkil adı veya Föy No ile ara..."
                    value={caseFileSearch}
                    onChange={(e) => setCaseFileSearch(e.target.value)}
                    style={{ marginBottom: '8px' }}
                  />
                  <div className="task-form-casefile-list" style={{ maxHeight: '165px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '6px', marginTop: '4px' }}>
                    {sortedAndFilteredCaseFiles.length === 0 ? (
                      <div style={{ padding: '10px 12px', color: '#6c757d', fontSize: '12px' }}>
                        {caseFileSearch.trim() ? 'Eşleşen dosya yok.' : 'Dosya bulunamadı.'}
                      </div>
                    ) : (
                      sortedAndFilteredCaseFiles.map((file) => {
                        const fileId = file.Id ?? file.id;
                        const foyNo = file.FoyNo ?? file.foyNo;
                        const muvekkil = file.Muvekkil ?? file.muvekkil;
                        const isSelected = formData.CaseFileId === fileId;
                        return (
                          <div
                            key={fileId}
                            role="button"
                            tabIndex={0}
                            onClick={() => setFormData(prev => ({ ...prev, CaseFileId: fileId }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormData(prev => ({ ...prev, CaseFileId: fileId })); } }}
                            style={{
                              padding: '8px 10px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: '12px',
                            }}
                          >
                            Foy No: {foyNo} - {muvekkil}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {formData.CaseFileId && (() => {
                    const selected = caseFiles.find(f => (f.Id ?? f.id) === formData.CaseFileId);
                    if (!selected) return null;
                    return (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#0d6efd' }}>
                        Seçilen: {selected.Muvekkil ?? selected.muvekkil} (Foy No: {selected.FoyNo ?? selected.foyNo})
                      </div>
                    );
                  })()}
                  <input type="hidden" name="CaseFileId" value={formData.CaseFileId || ''} />
                </div>
              )}

              <div className="form-group">
                <label>Atanacak Avukat *</label>
                <select
                  name="AssignedToUserId"
                  value={formData.AssignedToUserId}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  <option value="">Avukat Seçin</option>
                  {users.map((u) => {
                    const userId = u.Id ?? u.id;
                    const username = u.Username ?? u.username;
                    const name = u.Name ?? u.name ?? '';
                    const surname = u.Surname ?? u.surname ?? '';
                    const displayName = `${name} ${surname}`.trim() || username;
                    return (
                      <option key={userId} value={userId}>
                        {displayName} ({username})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="form-group">
                <label>Görev Açıklaması *</label>
                <textarea
                  name="Description"
                  value={formData.Description}
                  onChange={handleChange}
                  className="form-control"
                  rows="4"
                  placeholder="Görev açıklamasını yazın..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Görev Son Tarihi</label>
                <input
                  type="date"
                  name="DueDate"
                  value={formData.DueDate}
                  onChange={handleChange}
                  className="form-control"
                  min={getTodayDateString()}
                />
              </div>

            </>
          ) : isAdminEditing ? (
            <>
              <div className="form-group">
                <label>Atanacak Avukat *</label>
                <select
                  name="AssignedToUserId"
                  value={formData.AssignedToUserId}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  <option value="">Avukat Seçin</option>
                  {users.map((u) => {
                    const userId = u.Id ?? u.id;
                    const username = u.Username ?? u.username;
                    const name = u.Name ?? u.name ?? '';
                    const surname = u.Surname ?? u.surname ?? '';
                    const displayName = `${name} ${surname}`.trim() || username;
                    return (
                      <option key={userId} value={userId}>
                        {displayName} ({username})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="form-group">
                <label>Görev Açıklaması *</label>
                <textarea
                  name="Description"
                  value={formData.Description}
                  onChange={handleChange}
                  className="form-control"
                  rows="4"
                  placeholder="Görev açıklamasını yazın..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Görev Son Tarihi</label>
                <input
                  type="date"
                  name="DueDate"
                  value={formData.DueDate}
                  onChange={handleChange}
                  className="form-control"
                  min={getTodayDateString()}
                />
              </div>
            </>
          ) : (isCompleting || isAdminCompleting) ? (
            <>
              <div className="form-group">
                <label>Görev Açıklaması</label>
                <textarea
                  value={formData.Description}
                  className="form-control"
                  rows="3"
                  disabled
                />
              </div>

              <div className="form-group">
                <label>{isAdminCompleting ? 'Açıklama *' : 'Yapılan İşlem Açıklaması *'}</label>
                <textarea
                  name="Response"
                  value={formData.Response}
                  onChange={handleChange}
                  className="form-control"
                  rows="4"
                  placeholder={isAdminCompleting ? 'Yapılan işlem açıklaması...' : 'Görev için yaptığınız işlemleri açıklayın...'}
                  required
                />
              </div>
            </>
          ) : null}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading 
                ? 'Kaydediliyor...' 
                : isCreating 
                  ? 'Görev Ata' 
                  : isCompleting 
                    ? 'Kontrole Gönder' 
                    : isAdminCompleting 
                      ? 'Tamamlandı Yap' 
                      : isAdminEditing 
                        ? 'Kaydet' 
                        : 'Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;

